import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import api from '../services/api';
import './RelatorioOrdens.css';
import {
  FaFileExcel, FaFileCsv, FaFilePdf, FaTimes, FaFileExport, FaSpinner,
  FaLayerGroup, FaImages, FaChartLine, FaWhatsapp, FaSyncAlt, FaChevronDown,
} from 'react-icons/fa';

/**
 * RelatorioOrdens
 * ---------------------------------------------------------------------------
 * Gerador de relatórios de OS FECHADAS (todos os status finais), em 3 abas:
 *
 *   • Resumo    — filtros + KPIs + prévia agrupada + export XLSX/CSV/PDF.
 *   • Conteúdo  — "filtro avançado": puxa o conteúdo real de cada OS do
 *                 intervalo (linha do tempo / notas + evidências fotográficas)
 *                 e gera um DOSSIÊ PDF com as fotos embutidas de verdade
 *                 (via proxy autenticado /api/anexos/drive/:fileId).
 *   • Insights  — comparativo automático vs. período anterior + painel de
 *                 insights/anomalias (regras determinísticas, sem IA/backend)
 *                 + "Copiar resumo" pronto pro WhatsApp.
 *
 * Tudo 100% client-side: consome dados já carregados pelo FilaOrdens e
 * endpoints que já existem. NÃO altera nenhuma rota de backend.
 * Libs de XLSX/PDF carregadas SOB DEMANDA por CDN (fora do package.json).
 *
 * Modelo financeiro fiel ao backend: RECEITA (serviços concluídos) separada
 * de CUSTO (tarefas concluídas). Canceladas/Excluídas ficam fora do dinheiro.
 * ---------------------------------------------------------------------------
 */

// ── CDNs (carregadas sob demanda) ───────────────────────────────────────────
const CDN_XLSX      = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
const CDN_JSPDF     = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
const CDN_AUTOTABLE = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js';

const STATUS_FINAIS  = ['Concluído', 'Finalizada', 'Cancelado', 'Lançamento Excluído'];
const STATUS_RECEITA = ['Concluído', 'Finalizada'];
const MAX_IMG_PDF    = 8;   // máx. de fotos embutidas por OS no dossiê
const POOL_LIMIT     = 5;   // concorrência ao puxar notas/anexos

// ── Loader de script sob demanda ────────────────────────────────────────────
function carregarScript(src) {
  return new Promise((resolve, reject) => {
    const jaExiste = Array.from(document.getElementsByTagName('script')).some(s => s.src === src);
    if (jaExiste) { resolve(); return; }
    const el = document.createElement('script');
    el.src = src; el.async = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error(`Falha ao carregar biblioteca externa: ${src}`));
    document.head.appendChild(el);
  });
}

// ── Pool de concorrência ────────────────────────────────────────────────────
async function poolMap(items, limit, fn, onProgress) {
  const results = new Array(items.length);
  let cursor = 0, feito = 0;
  async function worker() {
    while (cursor < items.length) {
      const idx = cursor++;
      try { results[idx] = await fn(items[idx], idx); }
      catch { results[idx] = null; }
      feito++;
      if (onProgress) onProgress(feito, items.length);
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

// ── Helpers de formatação ───────────────────────────────────────────────────
const soData = (valor) => {
  if (!valor) return '';
  const s = String(valor);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};
const dataRefOS = (os) => soData(os.data_conclusao) || soData(os.data_criacao);

const fmtDataBR = (valor) => {
  const ymd = soData(valor);
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
};
const fmtDataHoraBR = (valor) => {
  if (!valor) return '';
  const d = new Date(valor);
  return Number.isNaN(d.getTime())
    ? fmtDataBR(valor)
    : d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const fmtMoeda = (n) => (Number(n) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDuracao = (segundos) => {
  const s = Number(segundos);
  if (!Number.isFinite(s) || s <= 0) return '';
  const h   = String(Math.floor(s / 3600)).padStart(2, '0');
  const m   = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const seg = String(Math.floor(s % 60)).padStart(2, '0');
  return `${h}:${m}:${seg}`;
};
const labelOrigem = (origem) => {
  switch (String(origem || 'interno').toLowerCase()) {
    case 'seguradora': return 'Seguradora';
    case 'app':        return 'Via App';
    default:           return 'Interno';
  }
};
const labelTipo = (tipo) => (String(tipo || 'servico') === 'tarefa' ? 'Tarefa' : 'Serviço');

const primeiroDiaMes = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
const hojeYMD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Aritmética de datas em UTC puro (sem shift de fuso)
const addDiasYMD = (ymd, delta) => {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return dt.toISOString().slice(0, 10);
};
const diffDiasYMD = (a, b) => {
  const da = new Date(a + 'T00:00:00Z'), db = new Date(b + 'T00:00:00Z');
  return Math.round((db - da) / 86400000);
};

// KPIs puros (reutilizado no período atual e no anterior)
function calcKpis(lista) {
  let servicos = 0, tarefas = 0, canceladas = 0, receita = 0, custo = 0, servConcl = 0;
  for (const os of lista) {
    const tipo = String(os.tipo_os || 'servico');
    const status = String(os.status || '').trim();
    const valor = Number(os.valor) || 0;
    const gera = STATUS_RECEITA.includes(status);
    if (status === 'Cancelado' || status === 'Lançamento Excluído') canceladas++;
    if (tipo === 'tarefa') { tarefas++; if (gera) custo += valor; }
    else { servicos++; if (gera) { receita += valor; servConcl++; } }
  }
  return {
    total: lista.length, servicos, tarefas, canceladas, receita, custo,
    ticket: servConcl > 0 ? receita / servConcl : 0,
  };
}

// Presets de agrupamento (aba Resumo)
const PRESETS = [
  { key: 'detalhado',  label: 'Detalhado'      },
  { key: 'dia',        label: 'Por Dia'        },
  { key: 'mes',        label: 'Por Mês'        },
  { key: 'resolvedor', label: 'Por Resolvedor' },
  { key: 'motorista',  label: 'Por Motorista'  },
  { key: 'cliente',    label: 'Por Cliente'    },
  { key: 'origem',     label: 'Por Origem'     },
  { key: 'tipo',       label: 'Por Tipo'       },
  { key: 'status',     label: 'Por Status'     },
];

// ── Imagem do Drive: thumb público → fallback proxy autenticado → cadeado ─────
function ImgDrive({ fileId, alt, getCfg }) {
  const [src, setSrc]       = useState(`https://drive.google.com/thumbnail?id=${fileId}&sz=w400`);
  const [estado, setEstado] = useState('drive'); // drive | proxy | erro
  const objUrlRef = useRef(null);

  useEffect(() => () => { if (objUrlRef.current) URL.revokeObjectURL(objUrlRef.current); }, []);

  const onErr = async () => {
    if (estado === 'drive') {
      try {
        const r = await api.get(`/api/anexos/drive/${fileId}`, { ...getCfg(), responseType: 'blob' });
        const u = URL.createObjectURL(r.data);
        objUrlRef.current = u;
        setSrc(u);
        setEstado('proxy');
      } catch { setEstado('erro'); }
    } else {
      setEstado('erro');
    }
  };

  if (estado === 'erro') return <div className="rel-img-fallback">🔒</div>;
  return <img className="rel-dossie-img" src={src} alt={alt} onError={onErr} loading="lazy" />;
}

export default function RelatorioOrdens({ isOpen, onClose, ordens = [], clientes = [], motoristas = [], user }) {
  const [aba, setAba]                   = useState('resumo'); // resumo | conteudo | insights
  const [de, setDe]                     = useState(primeiroDiaMes);
  const [ate, setAte]                   = useState(hojeYMD);
  const [statusSel, setStatusSel]       = useState(() => new Set(STATUS_FINAIS));
  const [tipoSel, setTipoSel]           = useState('todos');
  const [origemSel, setOrigemSel]       = useState('todos');
  const [motoristaSel, setMotoristaSel] = useState('');
  const [clienteSel, setClienteSel]     = useState('');
  const [groupBy, setGroupBy]           = useState('detalhado');

  const [usuariosMap, setUsuariosMap] = useState({});
  const [exportando, setExportando]   = useState(null); // 'xlsx' | 'csv' | 'pdf' | null
  const [erroExport, setErroExport]   = useState('');
  const [aviso, setAviso]             = useState('');

  // Conteúdo (dossiê)
  const [conteudoMap, setConteudoMap]           = useState({}); // { [osId]: {notas, anexos} }
  const [carregandoConteudo, setCarregando]     = useState(false);
  const [gerandoDossie, setGerandoDossie]       = useState(false);
  const [progresso, setProgresso]               = useState(null); // {feito,total,rotulo}
  const [expandido, setExpandido]               = useState(() => new Set());

  const authCfg = useCallback(() => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  // ── Mapas de nomes ────────────────────────────────────────────────────────
  const clienteNome = useCallback(
    (id) => clientes.find(c => c.id === id)?.nome || (id ? `Cliente #${id}` : '—'), [clientes]);
  const motoristaNome = useCallback(
    (id) => motoristas.find(m => m.id === id)?.nome || (id ? `Motorista #${id}` : '—'), [motoristas]);
  const resolvedorNome = useCallback((id) => {
    if (!id) return '—';
    if (usuariosMap[id]) return usuariosMap[id];
    const m = motoristas.find(x => x.id === id);
    return m?.nome || `Usuário #${id}`;
  }, [usuariosMap, motoristas]);

  // ── Enriquecimento de resolvedores (fail-open) ──────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    let ativo = true;
    (async () => {
      try {
        const r = await api.get('/api/usuarios/selecao', authCfg());
        const lista = Array.isArray(r.data) ? r.data : (r.data?.data || []);
        const mapa = {};
        for (const u of lista) {
          const rawId = u.id ?? u.value ?? u.usuario_id;
          const idNum = parseInt(String(rawId).replace(/\D/g, ''), 10);
          const nome  = u.nome ?? u.label ?? u.name;
          if (Number.isFinite(idNum) && nome) mapa[idNum] = nome;
        }
        if (ativo) setUsuariosMap(mapa);
      } catch { if (ativo) setUsuariosMap({}); }
    })();
    return () => { ativo = false; };
  }, [isOpen, authCfg]);

  // ── Recorte reutilizável (por janela de datas) ──────────────────────────────
  const recortar = useCallback((d1, d2) => {
    return ordens.filter((os) => {
      const status = String(os.status || '').trim();
      if (!STATUS_FINAIS.includes(status)) return false;
      if (!statusSel.has(status)) return false;
      const ref = dataRefOS(os);
      if (d1 && (!ref || ref < d1)) return false;
      if (d2 && (!ref || ref > d2)) return false;
      const tipo = String(os.tipo_os || 'servico');
      if (tipoSel !== 'todos' && tipo !== tipoSel) return false;
      const origem = String(os.origem || 'interno').toLowerCase();
      if (origemSel !== 'todos' && origem !== origemSel) return false;
      if (motoristaSel && os.motorista_id !== parseInt(motoristaSel, 10)) return false;
      if (clienteSel && os.cliente_id !== parseInt(clienteSel, 10)) return false;
      return true;
    });
  }, [ordens, statusSel, tipoSel, origemSel, motoristaSel, clienteSel]);

  const fechadas = useMemo(() => recortar(de, ate), [recortar, de, ate]);

  // Janela anterior (mesmo tamanho, imediatamente antes de `de`)
  const janelaAnterior = useMemo(() => {
    if (!de || !ate) return null;
    const len = diffDiasYMD(de, ate) + 1;
    if (len <= 0) return null;
    const prevAte = addDiasYMD(de, -1);
    const prevDe  = addDiasYMD(prevAte, -(len - 1));
    return { prevDe, prevAte, len };
  }, [de, ate]);

  const fechadasAnterior = useMemo(
    () => (janelaAnterior ? recortar(janelaAnterior.prevDe, janelaAnterior.prevAte) : []),
    [recortar, janelaAnterior]
  );

  const kpis = useMemo(() => calcKpis(fechadas), [fechadas]);

  // Invalida o conteúdo carregado quando o recorte muda
  useEffect(() => {
    setConteudoMap({});
    setExpandido(new Set());
  }, [de, ate, tipoSel, origemSel, motoristaSel, clienteSel, statusSel]);

  // ── Agrupamento (aba Resumo) ────────────────────────────────────────────────
  const chaveGrupo = useCallback((os) => {
    switch (groupBy) {
      case 'dia':        return fmtDataBR(dataRefOS(os)) || 'Sem data';
      case 'mes': {
        const ymd = dataRefOS(os);
        if (!ymd) return 'Sem data';
        const [y, m] = ymd.split('-');
        return `${m}/${y}`;
      }
      case 'resolvedor': return resolvedorNome(os.concluido_por_usuario_id);
      case 'motorista':  return motoristaNome(os.motorista_id);
      case 'cliente':    return clienteNome(os.cliente_id);
      case 'origem':     return labelOrigem(os.origem);
      case 'tipo':       return labelTipo(os.tipo_os);
      case 'status':     return String(os.status || '').trim() || '—';
      default:           return '';
    }
  }, [groupBy, resolvedorNome, motoristaNome, clienteNome]);

  const grupos = useMemo(() => {
    if (groupBy === 'detalhado') return [];
    const mapa = new Map();
    for (const os of fechadas) {
      const chave = chaveGrupo(os);
      if (!mapa.has(chave)) mapa.set(chave, { grupo: chave, qtd: 0, servicos: 0, tarefas: 0, canceladas: 0, receita: 0, custo: 0 });
      const g = mapa.get(chave);
      const tipo = String(os.tipo_os || 'servico');
      const status = String(os.status || '').trim();
      const valor = Number(os.valor) || 0;
      const gera = STATUS_RECEITA.includes(status);
      g.qtd++;
      if (status === 'Cancelado' || status === 'Lançamento Excluído') g.canceladas++;
      if (tipo === 'tarefa') { g.tarefas++; if (gera) g.custo += valor; }
      else { g.servicos++; if (gera) g.receita += valor; }
    }
    const arr = Array.from(mapa.values());
    if (groupBy === 'dia' || groupBy === 'mes') {
      arr.sort((a, b) => String(a.grupo).split('/').reverse().join('').localeCompare(String(b.grupo).split('/').reverse().join('')));
    } else {
      arr.sort((a, b) => b.qtd - a.qtd);
    }
    return arr;
  }, [fechadas, groupBy, chaveGrupo]);

  const linhasDetalhe = useMemo(() => fechadas.map((os) => ({
    'OS': String(os.id),
    'Status': String(os.status || '').trim(),
    'Tipo': labelTipo(os.tipo_os),
    'Origem': labelOrigem(os.origem),
    'Cliente': clienteNome(os.cliente_id),
    'Motorista': motoristaNome(os.motorista_id),
    'Resolvedor': resolvedorNome(os.concluido_por_usuario_id),
    'Local': os.local_atendimento || '',
    'Descrição': os.descricao || '',
    'Valor (R$)': Number(os.valor) || 0,
    'Criada em': fmtDataHoraBR(os.data_criacao),
    'Concluída em': fmtDataHoraBR(os.data_conclusao),
    'Duração': fmtDuracao(os.duracao_total_segundos_operacional_os),
  })), [fechadas, clienteNome, motoristaNome, resolvedorNome]);

  const linhasResumo = useMemo(() => grupos.map((g) => ({
    'Grupo': g.grupo, 'Qtd OS': g.qtd, 'Serviços': g.servicos, 'Tarefas': g.tarefas,
    'Canceladas': g.canceladas, 'Receita (R$)': g.receita, 'Custo (R$)': g.custo,
  })), [grupos]);

  const legendaFiltro = useMemo(() => {
    const partes = [`Período: ${fmtDataBR(de) || '—'} a ${fmtDataBR(ate) || '—'}`];
    if (tipoSel !== 'todos')   partes.push(`Tipo: ${labelTipo(tipoSel)}`);
    if (origemSel !== 'todos') partes.push(`Origem: ${labelOrigem(origemSel)}`);
    if (motoristaSel)          partes.push(`Motorista: ${motoristaNome(parseInt(motoristaSel, 10))}`);
    if (clienteSel)            partes.push(`Cliente: ${clienteNome(parseInt(clienteSel, 10))}`);
    if (STATUS_FINAIS.some(s => !statusSel.has(s))) partes.push(`Status: ${STATUS_FINAIS.filter(s => statusSel.has(s)).join(', ')}`);
    return partes.join('  •  ');
  }, [de, ate, tipoSel, origemSel, motoristaSel, clienteSel, statusSel, motoristaNome, clienteNome]);

  const nomeArquivo = useMemo(() => `Relatorio_OS_${de}_a_${ate}`, [de, ate]);

  // ── Análise: comparativo + insights ─────────────────────────────────────────
  const analise = useMemo(() => {
    const k  = kpis;
    const kp = calcKpis(fechadasAnterior);

    const comparativo = [
      { key: 'total',   lbl: 'OS Fechadas',   atual: k.total,      anterior: kp.total,      tipo: 'num',   bomSobe: true  },
      { key: 'receita', lbl: 'Receita',       atual: k.receita,    anterior: kp.receita,    tipo: 'moeda', bomSobe: true  },
      { key: 'custo',   lbl: 'Custo',         atual: k.custo,      anterior: kp.custo,      tipo: 'moeda', bomSobe: false },
      { key: 'ticket',  lbl: 'Ticket médio',  atual: k.ticket,     anterior: kp.ticket,     tipo: 'moeda', bomSobe: true  },
      { key: 'canc',    lbl: 'Canceladas',    atual: k.canceladas, anterior: kp.canceladas, tipo: 'num',   bomSobe: false },
    ];

    const insights = [];
    if (fechadas.length > 0) {
      const resolvedorCount = {}, motorista = {}, clienteRec = {}, dia = {};
      let somaDur = 0, nDur = 0;
      for (const os of fechadas) {
        const st = String(os.status || '').trim();
        const tipo = String(os.tipo_os || 'servico');
        const valor = Number(os.valor) || 0;
        const gera = STATUS_RECEITA.includes(st);
        const cancelada = st === 'Cancelado' || st === 'Lançamento Excluído';
        const rid = os.concluido_por_usuario_id;
        if (rid) resolvedorCount[rid] = (resolvedorCount[rid] || 0) + 1;
        const mid = os.motorista_id;
        if (mid) { motorista[mid] = motorista[mid] || { total: 0, canc: 0 }; motorista[mid].total++; if (cancelada) motorista[mid].canc++; }
        if (tipo !== 'tarefa' && gera) { const cid = os.cliente_id; if (cid) clienteRec[cid] = (clienteRec[cid] || 0) + valor; }
        const dref = dataRefOS(os); if (dref) dia[dref] = (dia[dref] || 0) + 1;
        const d = Number(os.duracao_total_segundos_operacional_os);
        if (gera && Number.isFinite(d) && d > 0) { somaDur += d; nDur++; }
      }

      const taxaCanc = k.total ? (k.canceladas / k.total * 100) : 0;
      insights.push(taxaCanc >= 20
        ? { tipo: 'alerta', icon: '⚠️', texto: `Cancelamento alto: ${taxaCanc.toFixed(0)}% das OS foram canceladas/excluídas no período.` }
        : { tipo: 'ok', icon: '✅', texto: `Cancelamento sob controle: ${taxaCanc.toFixed(0)}% do total.` });

      if (kp.receita > 0) {
        const dr = (k.receita - kp.receita) / kp.receita * 100;
        insights.push({
          tipo: dr >= 0 ? 'ok' : 'alerta',
          icon: dr >= 0 ? '📈' : '📉',
          texto: `Receita ${dr >= 0 ? 'subiu' : 'caiu'} ${Math.abs(dr).toFixed(0)}% vs. o período anterior (${fmtMoeda(kp.receita)} → ${fmtMoeda(k.receita)}).`,
        });
      }

      const topR = Object.entries(resolvedorCount).sort((a, b) => b[1] - a[1])[0];
      if (topR) insights.push({ tipo: 'info', icon: '🏆', texto: `Resolvedor mais ativo: ${resolvedorNome(parseInt(topR[0], 10))} — ${topR[1]} OS fechadas.` });

      const mArr = Object.entries(motorista)
        .filter(([, v]) => v.total >= 3)
        .map(([id, v]) => ({ id: parseInt(id, 10), ...v, taxa: v.canc / v.total }))
        .sort((a, b) => b.taxa - a.taxa);
      if (mArr[0] && mArr[0].taxa >= 0.3)
        insights.push({ tipo: 'alerta', icon: '🚩', texto: `${motoristaNome(mArr[0].id)} concentra cancelamentos: ${mArr[0].canc} de ${mArr[0].total} OS (${(mArr[0].taxa * 100).toFixed(0)}%).` });

      const topC = Object.entries(clienteRec).sort((a, b) => b[1] - a[1])[0];
      if (topC && topC[1] > 0) insights.push({ tipo: 'info', icon: '💎', texto: `Cliente que mais faturou: ${clienteNome(parseInt(topC[0], 10))} (${fmtMoeda(topC[1])}).` });

      const topDia = Object.entries(dia).sort((a, b) => b[1] - a[1])[0];
      if (topDia) insights.push({ tipo: 'info', icon: '📅', texto: `Dia de maior volume: ${fmtDataBR(topDia[0])} — ${topDia[1]} OS.` });

      if (nDur > 0) insights.push({ tipo: 'info', icon: '⏱️', texto: `Tempo médio de atendimento: ${fmtDuracao(Math.round(somaDur / nDur))} (${nDur} OS medidas).` });
    }

    return { comparativo, insights, kp };
  }, [kpis, fechadas, fechadasAnterior, resolvedorNome, motoristaNome, clienteNome]);

  const deltaPct = (atual, anterior) => {
    if (!anterior) return atual > 0 ? null : 0; // null => "novo"
    return (atual - anterior) / anterior * 100;
  };

  // ── Toggle de status ─────────────────────────────────────────────────────
  const toggleStatus = (s) => {
    setStatusSel((prev) => {
      const nx = new Set(prev);
      if (nx.has(s)) nx.delete(s); else nx.add(s);
      if (nx.size === 0) return prev;
      return nx;
    });
  };
  const toggleExpandido = (id) => {
    setExpandido((prev) => {
      const nx = new Set(prev);
      if (nx.has(id)) nx.delete(id); else nx.add(id);
      return nx;
    });
  };

  // ── Filtro avançado: puxa notas + anexos de cada OS do recorte ──────────────
  const carregarConteudo = useCallback(async () => {
    setErroExport(''); setAviso('');
    if (fechadas.length === 0) { setAviso('Nenhuma OS no recorte atual.'); return {}; }
    setCarregando(true);
    setProgresso({ feito: 0, total: fechadas.length, rotulo: 'Puxando conteúdo das OS' });
    try {
      const cfg = authCfg();
      const res = await poolMap(fechadas, POOL_LIMIT, async (os) => {
        const [n, a] = await Promise.all([
          api.get(`/api/ordens/${os.id}/notas`, cfg).then(r => r.data || []).catch(() => []),
          api.get(`/api/ordens/${os.id}/anexos`, cfg).then(r => r.data || []).catch(() => []),
        ]);
        return { id: os.id, notas: n, anexos: a };
      }, (f, t) => setProgresso({ feito: f, total: t, rotulo: 'Puxando conteúdo das OS' }));
      const mapa = {};
      for (const r of res) if (r) mapa[r.id] = { notas: r.notas, anexos: r.anexos };
      setConteudoMap(mapa);
      return mapa;
    } catch (e) {
      setErroExport(`Falha ao carregar conteúdo: ${e.message}`);
      return {};
    } finally {
      setCarregando(false);
      setProgresso(null);
    }
  }, [fechadas, authCfg]);

  const conteudoCarregado = Object.keys(conteudoMap).length > 0;

  // ── Exportações tabulares (aba Resumo) ──────────────────────────────────────
  const exportCSV = useCallback(() => {
    setErroExport(''); setExportando('csv');
    try {
      const usarResumo = groupBy !== 'detalhado';
      const linhas = usarResumo ? linhasResumo : linhasDetalhe;
      if (!linhas.length) { setExportando(null); return; }
      const cols = Object.keys(linhas[0]);
      const esc = (v) => { const s = String(v ?? ''); return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
      const corpo = [
        esc(`Relatório de OS Fechadas — ${legendaFiltro}`),
        cols.join(';'),
        ...linhas.map(l => cols.map(c => esc(l[c])).join(';')),
      ].join('\r\n');
      const blob = new Blob(['\uFEFF' + corpo], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${nomeArquivo}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setErroExport(`Falha ao gerar CSV: ${e.message}`); }
    finally { setExportando(null); }
  }, [groupBy, linhasResumo, linhasDetalhe, legendaFiltro, nomeArquivo]);

  const exportXLSX = useCallback(async () => {
    setErroExport(''); setExportando('xlsx');
    try {
      await carregarScript(CDN_XLSX);
      const XLSX = window.XLSX;
      if (!XLSX) throw new Error('Biblioteca de planilha indisponível.');
      const wb = XLSX.utils.book_new();
      const wsKpi = XLSX.utils.aoa_to_sheet([
        ['Relatório de OS Fechadas'], [legendaFiltro], [],
        ['Total de OS', kpis.total], ['Serviços', kpis.servicos], ['Tarefas', kpis.tarefas],
        ['Canceladas', kpis.canceladas], ['Receita (serviços)', kpis.receita],
        ['Custo (tarefas)', kpis.custo], ['Ticket médio', kpis.ticket],
      ]);
      wsKpi['!cols'] = [{ wch: 22 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(wb, wsKpi, 'Resumo Geral');

      if (analise.insights.length) {
        const wsIns = XLSX.utils.aoa_to_sheet([['Insights automáticos'], [], ...analise.insights.map(i => [i.texto])]);
        wsIns['!cols'] = [{ wch: 90 }];
        XLSX.utils.book_append_sheet(wb, wsIns, 'Insights');
      }
      if (linhasResumo.length) {
        const wsResumo = XLSX.utils.json_to_sheet(linhasResumo);
        wsResumo['!cols'] = Object.keys(linhasResumo[0]).map(k => ({ wch: k === 'Grupo' ? 32 : 14 }));
        XLSX.utils.book_append_sheet(wb, wsResumo, 'Agrupado');
      }
      if (linhasDetalhe.length) {
        const wsDet = XLSX.utils.json_to_sheet(linhasDetalhe);
        wsDet['!cols'] = Object.keys(linhasDetalhe[0]).map(k => ({ wch: k === 'Descrição' ? 40 : k === 'Local' ? 28 : k === 'Cliente' ? 24 : 16 }));
        XLSX.utils.book_append_sheet(wb, wsDet, 'Detalhado');
      }
      XLSX.writeFile(wb, `${nomeArquivo}.xlsx`);
    } catch (e) { setErroExport(`Falha ao gerar Excel: ${e.message}`); }
    finally { setExportando(null); }
  }, [kpis, legendaFiltro, linhasResumo, linhasDetalhe, nomeArquivo, analise]);

  const exportPDF = useCallback(async () => {
    setErroExport(''); setExportando('pdf');
    try {
      await carregarScript(CDN_JSPDF);
      await carregarScript(CDN_AUTOTABLE);
      const jsPDFCtor = window.jspdf?.jsPDF;
      if (!jsPDFCtor) throw new Error('Biblioteca de PDF indisponível.');
      const doc = new jsPDFCtor({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const margem = 40;
      let y = 46;
      doc.setFontSize(15); doc.setTextColor(15, 23, 42);
      doc.text('Relatório de OS Fechadas', margem, y); y += 18;
      doc.setFontSize(9); doc.setTextColor(100, 116, 139);
      doc.text(legendaFiltro, margem, y, { maxWidth: 760 }); y += 22;
      doc.setFontSize(9); doc.setTextColor(30, 41, 59);
      doc.text(
        `Total: ${kpis.total}   |   Serviços: ${kpis.servicos}   |   Tarefas: ${kpis.tarefas}   |   Canceladas: ${kpis.canceladas}   |   Receita: ${fmtMoeda(kpis.receita)}   |   Custo: ${fmtMoeda(kpis.custo)}   |   Ticket médio: ${fmtMoeda(kpis.ticket)}`,
        margem, y, { maxWidth: 760 }
      );
      y += 14;
      if (linhasResumo.length) {
        doc.autoTable({
          startY: y + 6,
          head: [Object.keys(linhasResumo[0])],
          body: linhasResumo.map(l => Object.keys(l).map(k => (k.includes('R$') ? fmtMoeda(l[k]) : String(l[k])))),
          styles: { fontSize: 8, cellPadding: 4 },
          headStyles: { fillColor: [59, 130, 246], textColor: 255 },
          margin: { left: margem, right: margem },
        });
        y = doc.lastAutoTable.finalY + 18;
      }
      if (linhasDetalhe.length) {
        const colsDet = ['OS', 'Status', 'Tipo', 'Origem', 'Cliente', 'Motorista', 'Resolvedor', 'Valor (R$)', 'Concluída em', 'Duração'];
        doc.autoTable({
          startY: y + 6,
          head: [colsDet],
          body: linhasDetalhe.map(l => colsDet.map(k => (k === 'Valor (R$)' ? fmtMoeda(l[k]) : String(l[k])))),
          styles: { fontSize: 7, cellPadding: 3, overflow: 'linebreak' },
          headStyles: { fillColor: [16, 185, 129], textColor: 255 },
          columnStyles: { 4: { cellWidth: 90 }, 5: { cellWidth: 80 }, 6: { cellWidth: 80 } },
          margin: { left: margem, right: margem },
        });
      }
      const totPag = doc.internal.getNumberOfPages();
      for (let p = 1; p <= totPag; p++) {
        doc.setPage(p);
        doc.setFontSize(8); doc.setTextColor(148, 163, 184);
        const larg = doc.internal.pageSize.getWidth();
        const alt = doc.internal.pageSize.getHeight();
        doc.text(`BLB System  •  ${new Date().toLocaleString('pt-BR')}`, margem, alt - 16);
        doc.text(`Página ${p} de ${totPag}`, larg - margem, alt - 16, { align: 'right' });
      }
      doc.save(`${nomeArquivo}.pdf`);
    } catch (e) { setErroExport(`Falha ao gerar PDF: ${e.message}`); }
    finally { setExportando(null); }
  }, [kpis, legendaFiltro, linhasResumo, linhasDetalhe, nomeArquivo]);

  // ── Dossiê PDF (aba Conteúdo): capa + notas + fotos embutidas ────────────────
  const carregarImgDims = (dataUrl) => new Promise((res) => {
    const im = new Image();
    im.onload = () => res({ w: im.naturalWidth, h: im.naturalHeight });
    im.onerror = () => res(null);
    im.src = dataUrl;
  });
  const jspdfFmt = (mime) => {
    const m = String(mime || '').toLowerCase();
    if (m.includes('png')) return 'PNG';
    if (m.includes('webp')) return 'WEBP';
    if (m.includes('jpeg') || m.includes('jpg')) return 'JPEG';
    return null;
  };
  const fetchImgDataUrl = useCallback(async (fileId) => {
    const r = await api.get(`/api/anexos/drive/${fileId}`, { ...authCfg(), responseType: 'blob' });
    const blob = r.data;
    const dataUrl = await new Promise((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => res(fr.result);
      fr.onerror = rej;
      fr.readAsDataURL(blob);
    });
    return { dataUrl, fmt: jspdfFmt(blob.type) };
  }, [authCfg]);

  const gerarDossiePDF = useCallback(async () => {
    setErroExport(''); setAviso('');
    if (fechadas.length === 0) { setAviso('Nenhuma OS no recorte atual.'); return; }
    setGerandoDossie(true);
    try {
      let mapa = conteudoMap;
      if (!Object.keys(mapa).length) mapa = await carregarConteudo();

      await carregarScript(CDN_JSPDF);
      await carregarScript(CDN_AUTOTABLE);
      const jsPDFCtor = window.jspdf?.jsPDF;
      if (!jsPDFCtor) throw new Error('Biblioteca de PDF indisponível.');

      const doc = new jsPDFCtor({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margem = 40;
      const contentW = pageW - 2 * margem;

      // ── CAPA ──
      let y = 60;
      doc.setFontSize(20); doc.setTextColor(15, 23, 42);
      doc.text('Dossiê de OS Fechadas', margem, y); y += 22;
      doc.setFontSize(10); doc.setTextColor(100, 116, 139);
      doc.text(legendaFiltro, margem, y, { maxWidth: contentW }); y += 16;
      doc.setFontSize(8);
      doc.text(`Gerado em ${new Date().toLocaleString('pt-BR')}  •  ${fechadas.length} OS`, margem, y); y += 22;

      doc.autoTable({
        startY: y,
        head: [['Indicador', 'Valor', 'Período anterior', 'Variação']],
        body: analise.comparativo.map((c) => {
          const d = deltaPct(c.atual, c.anterior);
          const fmt = (v) => (c.tipo === 'moeda' ? fmtMoeda(v) : String(v));
          const varTxt = d === null ? 'novo' : `${d >= 0 ? '+' : ''}${d.toFixed(0)}%`;
          return [c.lbl, fmt(c.atual), fmt(c.anterior), varTxt];
        }),
        styles: { fontSize: 9, cellPadding: 5 },
        headStyles: { fillColor: [59, 130, 246], textColor: 255 },
        margin: { left: margem, right: margem },
      });
      y = doc.lastAutoTable.finalY + 18;

      if (analise.insights.length) {
        doc.setFontSize(12); doc.setTextColor(15, 23, 42);
        doc.text('Insights automáticos', margem, y); y += 8;
        doc.setFontSize(9); doc.setTextColor(51, 65, 85);
        for (const ins of analise.insights) {
          const linhas = doc.splitTextToSize(`•  ${ins.texto}`, contentW);
          if (y + linhas.length * 12 > pageH - margem) { doc.addPage(); y = margem; }
          doc.text(linhas, margem, y + 10); y += linhas.length * 12 + 4;
        }
      }

      // ── contagem de imagens (progresso) ──
      let totalImgs = 0;
      for (const os of fechadas) {
        const c = mapa[os.id];
        if (c) totalImgs += Math.min((c.anexos || []).filter(a => (a.mime_type || '').startsWith('image/')).length, MAX_IMG_PDF);
      }
      let feitoImgs = 0;
      setProgresso({ feito: 0, total: totalImgs, rotulo: 'Montando dossiê e embutindo fotos' });

      // ── uma seção por OS ──
      for (const os of fechadas) {
        const c = mapa[os.id] || { notas: [], anexos: [] };
        doc.addPage(); y = margem;

        const meta = mapa[os.id];
        doc.setFillColor(59, 130, 246);
        doc.rect(margem, y, contentW, 26, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(12);
        doc.text(`OS #${os.id}  —  ${String(os.status || '').trim()}  —  ${clienteNome(os.cliente_id)}`, margem + 8, y + 17);
        y += 38;

        doc.setTextColor(51, 65, 85); doc.setFontSize(9);
        const infoLinhas = [
          `Tipo: ${labelTipo(os.tipo_os)}   |   Origem: ${labelOrigem(os.origem)}   |   Valor: ${fmtMoeda(os.valor)}`,
          `Motorista: ${motoristaNome(os.motorista_id)}   |   Resolvedor: ${resolvedorNome(os.concluido_por_usuario_id)}`,
          `Criada: ${fmtDataHoraBR(os.data_criacao)}   |   Concluída: ${fmtDataHoraBR(os.data_conclusao)}   |   Duração: ${fmtDuracao(os.duracao_total_segundos_operacional_os) || '—'}`,
          `Local: ${os.local_atendimento || '—'}`,
        ];
        for (const l of infoLinhas) {
          const wrapped = doc.splitTextToSize(l, contentW);
          doc.text(wrapped, margem, y); y += wrapped.length * 11 + 2;
        }
        if (os.descricao) {
          y += 2;
          const desc = doc.splitTextToSize(`Descrição: ${os.descricao}`, contentW);
          if (y + desc.length * 11 > pageH - margem) { doc.addPage(); y = margem; }
          doc.text(desc, margem, y); y += desc.length * 11 + 6;
        }

        // linha do tempo (notas)
        const notas = c.notas || [];
        doc.autoTable({
          startY: y + 4,
          head: [['Autor', 'Quando', 'Nota']],
          body: notas.length
            ? notas.map(n => [n.autor || '—', n.data_criacao ? fmtDataHoraBR(n.data_criacao) : '—', n.nota || ''])
            : [['—', '—', 'Sem notas registradas.']],
          styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
          headStyles: { fillColor: [100, 116, 139], textColor: 255 },
          columnStyles: { 0: { cellWidth: 90 }, 1: { cellWidth: 100 }, 2: { cellWidth: contentW - 190 } },
          margin: { left: margem, right: margem },
        });
        y = doc.lastAutoTable.finalY + 12;

        // evidências fotográficas (embutidas via proxy)
        const imgs = (c.anexos || []).filter(a => (a.mime_type || '').startsWith('image/')).slice(0, MAX_IMG_PDF);
        const totalImgsOS = (c.anexos || []).filter(a => (a.mime_type || '').startsWith('image/')).length;
        if (imgs.length) {
          if (y + 16 > pageH - margem) { doc.addPage(); y = margem; }
          doc.setFontSize(10); doc.setTextColor(15, 23, 42);
          doc.text(`Evidências fotográficas (${totalImgsOS})`, margem, y); y += 10;

          const gap = 10;
          const cellW = (contentW - gap) / 2;
          const cellH = cellW * 0.72;
          let col = 0;
          for (const a of imgs) {
            if (col === 0 && y + cellH > pageH - margem) { doc.addPage(); y = margem; }
            const x = margem + col * (cellW + gap);
            try {
              const { dataUrl, fmt } = await fetchImgDataUrl(a.drive_file_id);
              if (fmt) {
                const dims = await carregarImgDims(dataUrl);
                if (dims && dims.w > 0) {
                  const scale = Math.min(cellW / dims.w, cellH / dims.h);
                  const dw = dims.w * scale, dh = dims.h * scale;
                  doc.addImage(dataUrl, fmt, x + (cellW - dw) / 2, y + (cellH - dh) / 2, dw, dh);
                } else {
                  doc.addImage(dataUrl, fmt, x, y, cellW, cellH);
                }
                doc.setDrawColor(226, 232, 240); doc.rect(x, y, cellW, cellH);
              } else { throw new Error('formato'); }
            } catch {
              doc.setFillColor(248, 250, 252); doc.rect(x, y, cellW, cellH, 'F');
              doc.setTextColor(148, 163, 184); doc.setFontSize(8);
              doc.text('imagem indisponível', x + cellW / 2, y + cellH / 2, { align: 'center' });
            }
            feitoImgs++;
            setProgresso({ feito: feitoImgs, total: totalImgs, rotulo: 'Montando dossiê e embutindo fotos' });
            col = col === 0 ? 1 : 0;
            if (col === 0) y += cellH + gap;
          }
          if (col === 1) y += cellH + gap;
          if (totalImgsOS > MAX_IMG_PDF) {
            doc.setTextColor(148, 163, 184); doc.setFontSize(8);
            doc.text(`(+${totalImgsOS - MAX_IMG_PDF} foto(s) não incluída(s) para manter o tamanho do arquivo)`, margem, y + 8);
            y += 14;
          }
        }
        void meta;
      }

      const totPag = doc.internal.getNumberOfPages();
      for (let p = 1; p <= totPag; p++) {
        doc.setPage(p);
        doc.setFontSize(8); doc.setTextColor(148, 163, 184);
        doc.text(`BLB System  •  Dossiê de OS  •  ${new Date().toLocaleString('pt-BR')}`, margem, pageH - 16);
        doc.text(`Página ${p} de ${totPag}`, pageW - margem, pageH - 16, { align: 'right' });
      }
      doc.save(`Dossie_OS_${de}_a_${ate}.pdf`);
    } catch (e) {
      setErroExport(`Falha ao gerar dossiê: ${e.message}`);
    } finally {
      setGerandoDossie(false);
      setProgresso(null);
    }
  }, [fechadas, conteudoMap, carregarConteudo, analise, legendaFiltro, de, ate, clienteNome, motoristaNome, resolvedorNome, fetchImgDataUrl]);

  // ── Copiar resumo (WhatsApp) ────────────────────────────────────────────────
  const copiarResumo = useCallback(async () => {
    const linhas = [
      '📊 *Relatório de OS Fechadas*',
      legendaFiltro,
      '',
      `• Total: ${kpis.total}`,
      `• Serviços: ${kpis.servicos}  |  Tarefas: ${kpis.tarefas}  |  Canceladas: ${kpis.canceladas}`,
      `• Receita: ${fmtMoeda(kpis.receita)}  |  Custo: ${fmtMoeda(kpis.custo)}`,
      `• Ticket médio: ${fmtMoeda(kpis.ticket)}`,
    ];
    if (analise.insights.length) {
      linhas.push('', '*Insights:*', ...analise.insights.map(i => `${i.icon} ${i.texto}`));
    }
    const texto = linhas.join('\n');
    try {
      await navigator.clipboard.writeText(texto);
      setAviso('Resumo copiado! É só colar no WhatsApp.');
      setTimeout(() => setAviso(''), 3500);
    } catch {
      setErroExport('Não consegui acessar a área de transferência do navegador.');
    }
  }, [legendaFiltro, kpis, analise]);

  if (!isOpen) return null;

  const btnExport = (fmt, Icon, label, onClick, classe) => (
    <button className={`rel-btn ${classe}`} onClick={onClick}
      disabled={!!exportando || kpis.total === 0}
      style={{ opacity: (exportando && exportando !== fmt) || kpis.total === 0 ? 0.55 : 1 }}>
      {exportando === fmt ? <FaSpinner className="spin" size={13} /> : <Icon size={14} />}
      {label}
    </button>
  );

  const ocupado = carregandoConteudo || gerandoDossie;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="rel-modal" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="rel-header">
          <div className="rel-header-info">
            <FaFileExport size={18} color="#3b82f6" />
            <div>
              <h3>Relatório de OS Fechadas</h3>
              <p>Resumo, conteúdo completo (texto + fotos) e insights</p>
            </div>
          </div>
          <button className="rel-close" onClick={onClose}><FaTimes size={14} /></button>
        </div>

        {/* Body */}
        <div className="rel-body">

          {/* Filtros (compartilhados entre as abas) */}
          <div className="rel-filters">
            <div>
              <label className="rel-label">De</label>
              <input type="date" className="rel-input" value={de} max={ate} onChange={e => setDe(e.target.value)} />
            </div>
            <div>
              <label className="rel-label">Até</label>
              <input type="date" className="rel-input" value={ate} min={de} onChange={e => setAte(e.target.value)} />
            </div>
            <div>
              <label className="rel-label">Tipo</label>
              <select className="rel-select" value={tipoSel} onChange={e => setTipoSel(e.target.value)}>
                <option value="todos">Todos</option>
                <option value="servico">Serviço</option>
                <option value="tarefa">Tarefa</option>
              </select>
            </div>
            <div>
              <label className="rel-label">Origem</label>
              <select className="rel-select" value={origemSel} onChange={e => setOrigemSel(e.target.value)}>
                <option value="todos">Todas</option>
                <option value="interno">Interno</option>
                <option value="seguradora">Seguradora</option>
                <option value="app">Via App</option>
              </select>
            </div>
            <div>
              <label className="rel-label">Motorista</label>
              <select className="rel-select" value={motoristaSel} onChange={e => setMotoristaSel(e.target.value)}>
                <option value="">Todos</option>
                {motoristas.map(m => <option key={m.id_raw ?? m.id} value={m.id}>{m.nome}</option>)}
              </select>
            </div>
            <div>
              <label className="rel-label">Cliente</label>
              <select className="rel-select" value={clienteSel} onChange={e => setClienteSel(e.target.value)}>
                <option value="">Todos</option>
                {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
          </div>

          {/* Status chips */}
          <div className="rel-section">
            <label className="rel-label">Status incluídos</label>
            <div className="rel-chips">
              {STATUS_FINAIS.map(s => {
                const on = statusSel.has(s);
                return (
                  <button key={s} className={`rel-chip ${on ? 'on' : ''}`} onClick={() => toggleStatus(s)}>
                    {on ? '✓ ' : ''}{s === 'Finalizada' ? 'Concluído (App)' : s}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Abas */}
          <div className="rel-tabs">
            <button className={`rel-tab ${aba === 'resumo' ? 'on' : ''}`} onClick={() => setAba('resumo')}>
              <FaLayerGroup size={12} /> Resumo
            </button>
            <button className={`rel-tab ${aba === 'conteudo' ? 'on' : ''}`} onClick={() => setAba('conteudo')}>
              <FaImages size={12} /> Conteúdo
            </button>
            <button className={`rel-tab ${aba === 'insights' ? 'on' : ''}`} onClick={() => setAba('insights')}>
              <FaChartLine size={12} /> Insights
            </button>
          </div>

          {/* ══ ABA RESUMO ══ */}
          {aba === 'resumo' && (
            <>
              <div className="rel-section rel-section--presets">
                <label className="rel-label">Formato do relatório</label>
                <div className="rel-presets">
                  {PRESETS.map(p => (
                    <button key={p.key} className={`rel-preset ${groupBy === p.key ? 'on' : ''}`} onClick={() => setGroupBy(p.key)}>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rel-kpis">
                {[
                  { lbl: 'Total de OS',        val: kpis.total,             cor: '#3b82f6' },
                  { lbl: 'Serviços',           val: kpis.servicos,          cor: '#0ea5e9' },
                  { lbl: 'Tarefas',            val: kpis.tarefas,           cor: '#8b5cf6' },
                  { lbl: 'Canceladas',         val: kpis.canceladas,        cor: '#ef4444' },
                  { lbl: 'Receita (serviços)', val: fmtMoeda(kpis.receita), cor: '#10b981' },
                  { lbl: 'Custo (tarefas)',    val: fmtMoeda(kpis.custo),   cor: '#f59e0b' },
                  { lbl: 'Ticket médio',       val: fmtMoeda(kpis.ticket),  cor: '#64748b' },
                ].map((k, i) => (
                  <div key={i} className="rel-kpi">
                    <div className="rel-kpi-label">{k.lbl}</div>
                    <div className="rel-kpi-value" style={{ '--rel-kpi': k.cor }}>{k.val}</div>
                  </div>
                ))}
              </div>

              <div className="rel-preview">
                <div className="rel-preview-head">
                  Prévia — {groupBy === 'detalhado' ? `${linhasDetalhe.length} OS` : `${grupos.length} grupo(s)`}
                </div>
                <div className="rel-preview-scroll">
                  {kpis.total === 0 ? (
                    <div className="rel-empty">Nenhuma OS fechada encontrada com os filtros atuais.</div>
                  ) : groupBy === 'detalhado' ? (
                    <table className="rel-table">
                      <thead>
                        <tr>{['OS', 'Status', 'Tipo', 'Cliente', 'Resolvedor', 'Valor', 'Concluída'].map(h => <th key={h}>{h}</th>)}</tr>
                      </thead>
                      <tbody>
                        {linhasDetalhe.slice(0, 200).map((l, i) => (
                          <tr key={i}>
                            <td className="rel-td-strong">#{l['OS']}</td>
                            <td>{l['Status']}</td>
                            <td>{l['Tipo']}</td>
                            <td>{l['Cliente']}</td>
                            <td>{l['Resolvedor']}</td>
                            <td className="rel-td-semib">{fmtMoeda(l['Valor (R$)'])}</td>
                            <td className="rel-td-muted">{l['Concluída em']}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <table className="rel-table">
                      <thead>
                        <tr>
                          <th>Grupo</th><th className="rel-num">Qtd</th><th className="rel-num">Serv.</th>
                          <th className="rel-num">Taref.</th><th className="rel-num">Canc.</th>
                          <th className="rel-num">Receita</th><th className="rel-num">Custo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grupos.map((g, i) => (
                          <tr key={i}>
                            <td className="rel-td-semib">{g.grupo}</td>
                            <td className="rel-num rel-td-strong">{g.qtd}</td>
                            <td className="rel-num">{g.servicos}</td>
                            <td className="rel-num">{g.tarefas}</td>
                            <td className={`rel-num ${g.canceladas ? 'rel-danger' : 'rel-td-muted'}`}>{g.canceladas}</td>
                            <td className="rel-num rel-money-in">{fmtMoeda(g.receita)}</td>
                            <td className="rel-num rel-money-out">{fmtMoeda(g.custo)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                {groupBy === 'detalhado' && linhasDetalhe.length > 200 && (
                  <div className="rel-note">Exibindo as primeiras 200 OS na prévia. A exportação inclui todas as {linhasDetalhe.length}.</div>
                )}
              </div>
            </>
          )}

          {/* ══ ABA CONTEÚDO ══ */}
          {aba === 'conteudo' && (
            <div className="rel-conteudo">
              {!conteudoCarregado ? (
                <div className="rel-hint">
                  <FaImages size={26} color="#3b82f6" />
                  <p><strong>{fechadas.length} OS</strong> no recorte atual.</p>
                  <p>Clique em <strong>“Carregar conteúdo”</strong> (no rodapé) para puxar a linha do tempo (texto) e as evidências fotográficas de cada OS.</p>
                  {fechadas.length > 60 && (
                    <p className="rel-hint-warn">São muitas OS — o carregamento pode levar um tempo. Se quiser, estreite o período antes.</p>
                  )}
                </div>
              ) : (
                <div className="rel-dossie-list">
                  {fechadas.map((os) => {
                    const c = conteudoMap[os.id] || { notas: [], anexos: [] };
                    const imgs = (c.anexos || []).filter(a => (a.mime_type || '').startsWith('image/'));
                    const aberto = expandido.has(os.id);
                    return (
                      <div key={os.id} className={`rel-dossie-item ${aberto ? 'on' : ''}`}>
                        <button className="rel-dossie-head" onClick={() => toggleExpandido(os.id)}>
                          <span className="rel-dossie-id">#{os.id}</span>
                          <span className="rel-dossie-status">{String(os.status || '').trim()}</span>
                          <span className="rel-dossie-cli">{clienteNome(os.cliente_id)}</span>
                          <span className="rel-dossie-badges">
                            <span title="Notas">📝 {(c.notas || []).length}</span>
                            <span title="Fotos">🖼️ {imgs.length}</span>
                            <span className="rel-dossie-val">{fmtMoeda(os.valor)}</span>
                          </span>
                          <FaChevronDown className={`rel-dossie-chev ${aberto ? 'on' : ''}`} size={12} />
                        </button>
                        {aberto && (
                          <div className="rel-dossie-body">
                            <div className="rel-dossie-info">
                              <span><b>Tipo:</b> {labelTipo(os.tipo_os)}</span>
                              <span><b>Origem:</b> {labelOrigem(os.origem)}</span>
                              <span><b>Motorista:</b> {motoristaNome(os.motorista_id)}</span>
                              <span><b>Resolvedor:</b> {resolvedorNome(os.concluido_por_usuario_id)}</span>
                              <span><b>Concluída:</b> {fmtDataHoraBR(os.data_conclusao) || '—'}</span>
                              <span><b>Duração:</b> {fmtDuracao(os.duracao_total_segundos_operacional_os) || '—'}</span>
                              <span className="rel-dossie-full"><b>Local:</b> {os.local_atendimento || '—'}</span>
                              {os.descricao && <span className="rel-dossie-full"><b>Descrição:</b> {os.descricao}</span>}
                            </div>

                            <div className="rel-dossie-sub">Linha do tempo</div>
                            {(c.notas || []).length ? (
                              <div className="rel-timeline">
                                {c.notas.map((n) => (
                                  <div key={n.id} className="rel-timeline-item">
                                    <div className="rel-nota-topo">
                                      <span className="rel-nota-autor">{n.autor || '—'}</span>
                                      <span className="rel-nota-quando">{n.data_criacao ? fmtDataHoraBR(n.data_criacao) : ''}</span>
                                    </div>
                                    <p className="rel-nota-texto">{n.nota}</p>
                                  </div>
                                ))}
                              </div>
                            ) : <div className="rel-empty-sm">Sem notas registradas.</div>}

                            <div className="rel-dossie-sub">Evidências fotográficas ({imgs.length})</div>
                            {imgs.length ? (
                              <div className="rel-dossie-imgs">
                                {imgs.map((a) => (
                                  <ImgDrive key={a.id} fileId={a.drive_file_id} alt={a.nome_arquivo} getCfg={authCfg} />
                                ))}
                              </div>
                            ) : <div className="rel-empty-sm">Nenhuma foto anexada.</div>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ══ ABA INSIGHTS ══ */}
          {aba === 'insights' && (
            <div className="rel-insights-wrap">
              <div className="rel-compare-head">
                Comparativo vs. período anterior
                {janelaAnterior && (
                  <span className="rel-compare-sub">
                    ({fmtDataBR(janelaAnterior.prevDe)} a {fmtDataBR(janelaAnterior.prevAte)} — {janelaAnterior.len} dia(s))
                  </span>
                )}
              </div>
              <div className="rel-compare">
                {analise.comparativo.map((c) => {
                  const d = deltaPct(c.atual, c.anterior);
                  const fmt = (v) => (c.tipo === 'moeda' ? fmtMoeda(v) : String(v));
                  let dir = 'flat', bom = true, txt = '—';
                  if (d === null) { dir = 'up'; bom = c.bomSobe; txt = 'novo'; }
                  else if (d > 0) { dir = 'up'; bom = c.bomSobe; txt = `+${d.toFixed(0)}%`; }
                  else if (d < 0) { dir = 'down'; bom = !c.bomSobe; txt = `${d.toFixed(0)}%`; }
                  return (
                    <div key={c.key} className="rel-compare-card">
                      <div className="rel-compare-lbl">{c.lbl}</div>
                      <div className="rel-compare-val">{fmt(c.atual)}</div>
                      <div className={`rel-compare-delta ${dir} ${bom ? 'bom' : 'ruim'}`}>
                        {dir === 'up' ? '▲' : dir === 'down' ? '▼' : '■'} {txt}
                      </div>
                      <div className="rel-compare-prev">antes: {fmt(c.anterior)}</div>
                    </div>
                  );
                })}
              </div>

              <div className="rel-compare-head" style={{ marginTop: 18 }}>Insights automáticos</div>
              {analise.insights.length ? (
                <div className="rel-insights">
                  {analise.insights.map((ins, i) => (
                    <div key={i} className={`rel-insight rel-insight--${ins.tipo}`}>
                      <span className="rel-insight-icon">{ins.icon}</span>
                      <span>{ins.texto}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rel-empty">Sem dados suficientes para gerar insights neste recorte.</div>
              )}
            </div>
          )}

          {/* Progresso */}
          {progresso && (
            <div className="rel-progress">
              <div className="rel-progress-top">
                <span>{progresso.rotulo}…</span>
                <span>{progresso.feito}/{progresso.total}</span>
              </div>
              <div className="rel-progress-track">
                <div className="rel-progress-bar" style={{ width: `${progresso.total ? (progresso.feito / progresso.total * 100) : 0}%` }} />
              </div>
            </div>
          )}

          {aviso && <div className="rel-ok-msg">✓ {aviso}</div>}
          {erroExport && <div className="rel-error">⚠️ {erroExport}</div>}
        </div>

        {/* Footer — ações dependem da aba */}
        <div className="rel-footer">
          <span className="rel-count">{kpis.total} OS no recorte atual</span>
          <div className="rel-actions">
            {aba === 'resumo' && (
              <>
                {btnExport('xlsx', FaFileExcel, 'Excel', exportXLSX, 'rel-btn--xlsx')}
                {btnExport('csv', FaFileCsv, 'CSV', exportCSV, 'rel-btn--csv')}
                {btnExport('pdf', FaFilePdf, 'PDF', exportPDF, 'rel-btn--pdf')}
              </>
            )}

            {aba === 'conteudo' && (
              <>
                {!conteudoCarregado ? (
                  <button className="rel-btn rel-btn--load" onClick={carregarConteudo} disabled={ocupado || kpis.total === 0}>
                    {carregandoConteudo ? <FaSpinner className="spin" size={13} /> : <FaImages size={14} />}
                    Carregar conteúdo ({fechadas.length} OS)
                  </button>
                ) : (
                  <>
                    <button className="rel-btn rel-btn--ghost" onClick={carregarConteudo} disabled={ocupado} title="Recarregar conteúdo">
                      <FaSyncAlt size={13} /> Recarregar
                    </button>
                    <button className="rel-btn rel-btn--dossie" onClick={gerarDossiePDF} disabled={ocupado || kpis.total === 0}>
                      {gerandoDossie ? <FaSpinner className="spin" size={13} /> : <FaFilePdf size={14} />}
                      Gerar Dossiê PDF (com fotos)
                    </button>
                  </>
                )}
              </>
            )}

            {aba === 'insights' && (
              <>
                <button className="rel-btn rel-btn--wa" onClick={copiarResumo} disabled={kpis.total === 0}>
                  <FaWhatsapp size={14} /> Copiar resumo
                </button>
                {btnExport('xlsx', FaFileExcel, 'Excel (c/ insights)', exportXLSX, 'rel-btn--xlsx')}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}